import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Ticket,
  Search,
  Loader2,
  Clock,
  MessageSquare,
  Send,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Mail,
  ArrowLeft,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useNotifications } from "../hooks/useNotifications";

const STATUSES = {
  a_traiter: { label: "En attente", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  en_traitement: { label: "En cours", color: "bg-blue-100 text-blue-800", icon: AlertTriangle },
  regle: { label: "Résolu", color: "bg-green-100 text-green-800", icon: CheckCircle },
};

const getStatusBadge = (status) => {
  const statusInfo = STATUSES[status] || STATUSES.a_traiter;
  const Icon = statusInfo.icon;
  return (
    <Badge className={`${statusInfo.color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {statusInfo.label}
    </Badge>
  );
};

const MyTickets = ({ open, onOpenChange, userEmail = "" }) => {
  const [email, setEmail] = useState(userEmail);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // WebSocket notifications - auto refresh when notification received
  const handleNotification = useCallback((data) => {
    if (data.type === 'ticket_response' || data.type === 'ticket_status_change') {
      // Auto-refresh tickets when we get a notification
      if (hasSearched && email) {
        fetchTicketsQuiet();
      }
    }
  }, [hasSearched, email]);

  const { isConnected } = useNotifications(
    hasSearched && email ? email : null, 
    handleNotification
  );

  const fetchTickets = useCallback(async () => {
    if (!email || !email.includes("@")) {
      toast.error("Veuillez entrer une adresse email valide");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/support/my-tickets`, {
        params: { email: email.toLowerCase().trim() }
      });
      setTickets(response.data);
      setHasSearched(true);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast.error("Erreur lors de la récupération de vos tickets");
    }
    setLoading(false);
  }, [email]);

  // Silent fetch for auto-refresh (no loading indicator)
  const fetchTicketsQuiet = useCallback(async () => {
    if (!email || !email.includes("@")) return;
    
    try {
      const response = await axios.get(`${API}/support/my-tickets`, {
        params: { email: email.toLowerCase().trim() }
      });
      setTickets(response.data);
      
      // If we have a selected ticket, refresh it too
      if (selectedTicket) {
        const detailResponse = await axios.get(
          `${API}/support/my-tickets/${selectedTicket.id}`,
          { params: { email: email.toLowerCase().trim() } }
        );
        setSelectedTicket(detailResponse.data);
      }
    } catch (error) {
      console.error("Error fetching tickets quietly:", error);
    }
  }, [email, selectedTicket]);

  useEffect(() => {
    if (userEmail && open) {
      setEmail(userEmail);
    }
  }, [userEmail, open]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || newMessage.length < 5) {
      toast.error("Votre message doit contenir au moins 5 caractères");
      return;
    }

    setSendingMessage(true);
    try {
      await axios.post(
        `${API}/support/my-tickets/${selectedTicket.id}/reply`,
        null,
        {
          params: {
            email: email.toLowerCase().trim(),
            message: newMessage.trim()
          }
        }
      );
      
      toast.success("Votre message a été envoyé!");
      setNewMessage("");
      
      // Refresh ticket details
      const response = await axios.get(
        `${API}/support/my-tickets/${selectedTicket.id}`,
        { params: { email: email.toLowerCase().trim() } }
      );
      setSelectedTicket(response.data);
      
      // Refresh tickets list
      fetchTickets();
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Erreur lors de l'envoi de votre message");
    }
    setSendingMessage(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-CA", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-brand-600" />
              Mes tickets de support
            </div>
            {hasSearched && (
              <div className="flex items-center gap-1 text-xs font-normal">
                {isConnected ? (
                  <>
                    <Wifi className="h-3 w-3 text-green-500" />
                    <span className="text-green-600">Live</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="h-3 w-3 text-gray-400" />
                    <span className="text-gray-400">Hors ligne</span>
                  </>
                )}
              </div>
            )}
          </DialogTitle>
          <DialogDescription>
            Consultez et suivez vos demandes de support
            {hasSearched && isConnected && (
              <span className="text-green-600 ml-1">• Notifications en temps réel activées</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {selectedTicket ? (
          /* Ticket Detail View */
          <div className="flex-1 overflow-hidden flex flex-col">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedTicket(null)}
              className="self-start mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>

            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Ticket Header */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-gray-900">{selectedTicket.subject}</h3>
                  {getStatusBadge(selectedTicket.status)}
                </div>
                <p className="text-sm text-gray-500">
                  Créé le {formatDate(selectedTicket.created_at)}
                </p>
              </div>

              {/* Original Description */}
              <div className="bg-white border rounded-lg p-4">
                <h4 className="font-medium text-sm text-gray-500 mb-2">Votre demande initiale</h4>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              {/* Conversation Thread */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-500 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Conversation ({selectedTicket.responses?.length || 0} messages)
                </h4>

                {selectedTicket.responses?.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                    <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>En attente d'une réponse de notre équipe</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {selectedTicket.responses?.map((response) => (
                      <div
                        key={response.id}
                        className={`p-3 rounded-lg ${
                          response.is_customer_reply
                            ? "bg-brand-50 border-l-4 border-brand-500 ml-4"
                            : "bg-blue-50 border-l-4 border-blue-500 mr-4"
                        }`}
                      >
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span className="font-medium">
                            {response.is_customer_reply ? "Vous" : response.author_name || "Support Middleman"}
                          </span>
                          <span>{formatDate(response.created_at)}</span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap text-sm">{response.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reply Form */}
              {selectedTicket.status !== "regle" && (
                <div className="bg-white border rounded-lg p-4 mt-4">
                  <Label className="text-sm font-medium mb-2 block">Envoyer un message</Label>
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Écrivez votre message ici..."
                    rows={3}
                    className="mb-3"
                    data-testid="ticket-reply-input"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || newMessage.length < 5}
                    className="w-full bg-brand-600 hover:bg-brand-700"
                    data-testid="ticket-send-reply"
                  >
                    {sendingMessage ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Envoyer
                      </>
                    )}
                  </Button>
                </div>
              )}

              {selectedTicket.status === "regle" && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-green-800 font-medium">Ce ticket a été résolu</p>
                  <p className="text-green-600 text-sm">
                    Si vous avez d'autres questions, créez un nouveau ticket.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Tickets List View */
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Email Search */}
            <div className="space-y-3 mb-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Votre adresse email"
                    className="pl-10"
                    data-testid="my-tickets-email"
                    onKeyPress={(e) => e.key === "Enter" && fetchTickets()}
                  />
                </div>
                <Button
                  onClick={fetchTickets}
                  disabled={loading || !email.includes("@")}
                  className="bg-brand-600 hover:bg-brand-700"
                  data-testid="my-tickets-search"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
                {hasSearched && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={fetchTickets}
                    disabled={loading}
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Entrez l'email utilisé lors de la création de vos tickets
              </p>
            </div>

            {/* Tickets List */}
            <div className="flex-1 overflow-y-auto space-y-3">
              {!hasSearched ? (
                <div className="text-center py-12 text-gray-500">
                  <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Entrez votre email pour voir vos tickets</p>
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun ticket trouvé pour cet email</p>
                </div>
              ) : (
                tickets.map((ticket) => (
                  <Card
                    key={ticket.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedTicket(ticket)}
                    data-testid={`my-ticket-${ticket.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900 truncate flex-1">
                          {ticket.subject}
                        </h3>
                        {getStatusBadge(ticket.status)}
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                        {ticket.description}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {ticket.responses?.length || 0} réponse(s)
                        </span>
                        <span>{formatDate(ticket.created_at)}</span>
                      </div>
                      {ticket.responses?.length > 0 && !ticket.responses.some(r => r.is_customer_reply) && (
                        <div className="mt-2 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          Nouvelle réponse de l'équipe
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MyTickets;
